import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { TraceLevel } from "./events/Trace.event";
import { scrubUrls } from "./utilities/UrlScrubber";
export class UnhandledErrorHandler {
    /**
     * Register an unhandled error handler that will catch all errors that are not caught by a try/catch block
     * @param traceLogger trace logger
     * @param disableCallStackInUnhandledErrorLog Whether to disable logging call stack in unhandled error handler
     */ static registerUnhandledErrorHandler(traceLogger, disableCallStackInUnhandledErrorLog, disableMessageInUnhandledErrorLog) {
        // Abort previous listener to prevent stacking duplicate handlers
        UnhandledErrorHandler.abortController?.abort();
        UnhandledErrorHandler.abortController = new AbortController();
        const handler = new UnhandledErrorHandler(traceLogger, disableCallStackInUnhandledErrorLog, disableMessageInUnhandledErrorLog);
        window.onerror = handler.handleOnError.bind(handler);
        window.addEventListener("unhandledrejection", handler.handleUnhandledRejection.bind(handler), {
            signal: UnhandledErrorHandler.abortController.signal
        });
    }
    /**
     * Takes a string and, if it is longer than maxLength, truncates it and adds ellipsis
     * @param The string to shorten
     * @param The maximum length of the string returned by this function
     * @return A string of length &lt;= maxLength, with "..." appended if there was truncation
     */ static shortenStringAddEllipsis(str, maxLength) {
        if (str && str.length > maxLength) {
            str = str.substr(0, maxLength - 3) + "...";
        }
        return str;
    }
    /**
     * Given a function object, return the name of the function
     * For anonymous or minified functions, take the first few characters of the body
     * @param The function object
     * @return The name of the function
     */ static getMethodName(method) {
        let methodSignature;
        if (method) {
            // Pull out the method signature.
            let methodStr = method?.toString?.() ?? "InvalidMethod()";
            const functionNameStartIndex = methodStr.indexOf(" ") === 8 ? 9 : 0; // If the format is "function <name>(...", leave "function" out of our returned string
            const functionNameEndIndex = methodStr.indexOf(")") + 1;
            methodSignature = methodStr.substring(functionNameStartIndex, functionNameEndIndex);
            // Anonymous or minified function, include 50 characters of the body so we can identify the function
            if (methodSignature.indexOf("function") === 0 || methodSignature.indexOf("(") < 3) {
                const totalLength = functionNameEndIndex + UnhandledErrorHandler.MAXCHARACTEROFANONYMOUSFUNCTION;
                methodStr = methodStr.replace(/\s\s*/g, " ");
                methodSignature = UnhandledErrorHandler.shortenStringAddEllipsis(methodStr, totalLength) + (totalLength < methodStr.length ? "}" : "");
            }
        }
        return methodSignature;
    }
    /**
     * Get the current call stack as an array of strings, each element being one level of the stack
     */ getStackAsArray() {
        // Try to get the stack from a partner callback
        const stack = [];
        try {
            // Throw an error to get the stack trace
            throw new Error();
        } catch (ex) {
            // Parse the stack trace
            const stackTrace = ex.stack.split("\n");
            // Skip the first line (the error message) and the second line (this function)
            for(let i = 2; i < stackTrace.length && i < UnhandledErrorHandler.MAXCALLSTACKLEVELS + 2; i++){
                // Add the method name to the stack array
                const match = stackTrace[i].match(/at ([^\s(]+[\s(])/);
                const methodName = match ? match[1] : undefined;
                stack.push(methodName);
            }
        }
        return stack;
    }
    /**
     * Collect data about the error and send it off to the server
     * @param The error message the browser assigned to the Error
     * @param The URL to the js file that caused the error, or the page if the JS file is unavailable
     * @param The the js line number
     * @param The the js column number
     * @param The error object
     */ handleOnError(message, jsUrl, jsLine, jsCol, errorObject) {
        // This is to support TAB test framework catching product exceptions during test runs
        try {
            if (window.parent?.opener && "LogOnError" in window.parent.opener) {
                Function.prototype.apply.call(window.parent.opener["LogOnError"], window.parent.opener, [
                    message,
                    jsUrl,
                    jsLine,
                    jsCol,
                    errorObject
                ]);
            }
        } catch (e) {
        // don't do anything
        }
        // Get the callstack from the error object and using the arguments.callee.caller method
        // (second will only work in IE)
        let stack = errorObject?.stack;
        let builtStack = this.getStackAsArray().join("\n");
        // Remove >1 consecutive whitespace and truncate to MAXCALLSTACKLEVELS lines
        if (stack) {
            stack = stack.replace(/[ \t]{2,}/g, " ").split("\n").slice(0, UnhandledErrorHandler.MAXCALLSTACKLEVELS).join("\n");
        }
        builtStack = builtStack.replace(/[ \t]{2,}/g, " ");
        // Classify the error for structured diagnostics without logging PII
        const { errorClass, metadata } = errorObject ? ErrorUtilities.classifyError(errorObject) : {
            errorClass: "UnknownError",
            metadata: {}
        };
        if (this.errorCount < UnhandledErrorHandler.MAXERRORS) {
            this.traceLogger?.logTrace(0x1e44e7a2 /* tag_4ro48 */ , TraceLevel.Error, "Unhandled error", {
                errorClass,
                ...metadata,
                ...!this.disableCallStackInUnhandledErrorLog && {
                    callStack: stack || builtStack
                },
                ...!this.disableMessageInUnhandledErrorLog && {
                    errorMessage: scrubUrls(message)
                },
                col: `${jsCol}`,
                line: `${jsLine}`
            });
        } else if (this.errorCount === UnhandledErrorHandler.MAXERRORS) {
            this.traceLogger?.logTrace(0x1e44e7a1 /* tag_4ro47 */ , TraceLevel.Error, `Unhandled error - Hit max error limit: [ErrorLimit=${UnhandledErrorHandler.MAXERRORS}]`);
        }
        this.errorCount++;
    }
    /**
     * Handle unhandled promise rejections. Shares the same errorCount budget as
     * handleOnError — once MAXERRORS is reached across both handlers, further
     * errors are silently dropped to protect telemetry throughput.
     * @param event The PromiseRejectionEvent
     */ handleUnhandledRejection(event) {
        // Already past the limit — drop silently
        if (this.errorCount > UnhandledErrorHandler.MAXERRORS) {
            return;
        }
        // Exactly at the limit — log one final "truncated" message, then stop
        if (this.errorCount === UnhandledErrorHandler.MAXERRORS) {
            this.traceLogger?.logTrace(0x1e01f1c9 /* tag_4a5hj */ , TraceLevel.Error, `Unhandled rejection - Hit max error limit: [ErrorLimit=${UnhandledErrorHandler.MAXERRORS}]`);
            this.errorCount++;
            return;
        }
        const reason = event.reason;
        // Use error classification instead of raw messages to avoid PII leakage
        if (reason instanceof Error) {
            const { errorClass, metadata } = ErrorUtilities.classifyError(reason);
            this.traceLogger?.logTrace(0x1e01f1c8 /* tag_4a5hi */ , TraceLevel.Error, "Unhandled promise rejection", {
                errorClass,
                ...metadata
            });
        } else {
            // Non-Error reasons (strings, objects) — log only the type, not the value
            this.traceLogger?.logTrace(0x1e01e684 /* tag_4a40e */ , TraceLevel.Error, "Unhandled promise rejection", {
                errorClass: "NonErrorRejection",
                reasonType: typeof reason
            });
        }
        this.errorCount++;
    }
    /**
     * Create an UnhandledErrorHandler
     * @param traceLogger Optional trace logger
     * @param disableCallStackInUnhandledErrorLog Whether to disable logging call stack in unhandled error handler
     * @param disableMessageInUnhandledErrorLog Whether to disable logging the error message in unhandled error handler
     */ constructor(traceLogger, disableCallStackInUnhandledErrorLog, disableMessageInUnhandledErrorLog){
        this.errorCount = 0;
        this.traceLogger = traceLogger;
        this.disableCallStackInUnhandledErrorLog = disableCallStackInUnhandledErrorLog ?? false;
        this.disableMessageInUnhandledErrorLog = disableMessageInUnhandledErrorLog ?? false;
    }
}
UnhandledErrorHandler.MAXCALLSTACKLEVELS = 10;
UnhandledErrorHandler.MAXCHARACTEROFANONYMOUSFUNCTION = 50;
/**
     * Maximum number of errors to log per session. After this limit, errors are silently
     * dropped to prevent a runaway error loop from flooding telemetry pipelines and
     * degrading performance. A single "hit max limit" message is logged at the threshold
     * so we know truncation occurred. The count is shared between window.onerror and
     * unhandledrejection — both decrement the same budget.
     */ UnhandledErrorHandler.MAXERRORS = 100;
